'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useMemo } from 'react';
import { useRetentionStrategy } from '@/app/components/shared/RetentionStrategy';
import { SITE } from '@/app/constants';
import { useUserNexus } from '@/app/hooks';
import { cn } from '@/app/utils';

const FIRST_DISCOUNT_BG =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/discount-banner-bg-83d2d48b.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/discount-banner-bg-83d2d48b.webp';

const LAST_7D_BG =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/last-7d-banner-bg-d49b556e.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/last-7d-banner-bg-d49b556e.webp';

const LAST_1D_BG =
  SITE === 'china'
    ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/last-1d-banner-bg-fe913555.webp'
    : 'https://image01.cf.vidu.studio/vidu/media-asset/last-1d-banner-bg-fe913555.webp';

const bannerClass =
  'relative flex h-[120px] w-full flex-col items-center justify-center gap-4 gap-y-2 overflow-hidden rounded-16 bg-black bg-center bg-cover bg-no-repeat sm:h-[100px] sm:gap-2';

const bgStyle = (url: string) => ({ backgroundImage: `url(${url})` });

const pad2 = (value: number) => String(value).padStart(2, '0');

const AnimatedDigit: FC<{ digit: string }> = ({ digit }) => (
  <span className="relative inline-flex h-[1em] w-[0.6em] overflow-hidden">
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.span
        key={digit}
        initial={{ y: '-100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: '100%', opacity: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="absolute inset-0 flex items-center justify-center"
      >
        {digit}
      </motion.span>
    </AnimatePresence>
  </span>
);

const TimeBlock = ({ value, label }: { value: string; label: string }) => (
  <div className="flex shrink-0 flex-col items-center justify-center gap-0.5 rounded-16 bg-system-black24 p-3 backdrop-blur-[10px] sm:p-2">
    <div className="flex items-center gap-0.5 font-bold text-[42px] text-white tabular-nums leading-none sm:text-2xl">
      <AnimatedDigit digit={value[0]} />
      <AnimatedDigit digit={value[1]} />
    </div>
    <div className="text-sm text-system-white40 sm:text-[10px]">{label}</div>
  </div>
);

/**
 * 是否命中折扣活动（首单优惠 / 到期挽留）。
 * 供订阅页 banner 轮播判断是否需要渲染折扣位，避免与 DiscountBanner 的展示条件产生分歧。
 */
export const useHasDiscountBanner = (): boolean => {
  const { isSubOneTimeOffer } = useUserNexus();
  const { phase: retentionPhase } = useRetentionStrategy();
  return isSubOneTimeOffer || retentionPhase === 'phase2' || retentionPhase === 'phase3';
};

export const DiscountBanner: FC<{ className?: string }> = ({ className }) => {
  const { isSubOneTimeOffer } = useUserNexus();
  const {
    countdownHours,
    countdownMinutes,
    countdownSeconds,
    phase: retentionPhase,
    daysToExpiry,
  } = useRetentionStrategy();
  const t = useTranslations();

  const is7DaysLeft = retentionPhase === 'phase2';
  const isLastDay = retentionPhase === 'phase3';

  const isDiscountActivity = isSubOneTimeOffer || isLastDay || is7DaysLeft;

  // 数字段落在设计稿里是描边胶囊，按状态取不同的描边/底色
  const highlight = useMemo(
    () => (chunks: ReactNode, tone: 'amber' | 'rose') => (
      <span
        className={cn(
          'mx-1 inline-flex items-center justify-center rounded-12 border px-3 py-1 align-middle sm:mx-0.5 sm:px-2 sm:py-1',
          tone === 'amber'
            ? 'border-[#3a2c22] bg-[#2e1500] shadow-[inset_0_0_10px_0_rgba(200,52,36,0.8)]'
            : 'border-[#452528] bg-[#42141a] shadow-[inset_0_0_12px_0_rgba(200,20,50,0.66)]',
        )}
      >
        {chunks}
      </span>
    ),
    [],
  );

  if (!isDiscountActivity) return null;

  // 首单优惠：小字 + 大字居中堆叠
  if (isSubOneTimeOffer) {
    return (
      <div className={cn(bannerClass, 'px-6 sm:px-4', className)} style={bgStyle(FIRST_DISCOUNT_BG)}>
        <div className="text-center font-semibold text-2xl text-system-white80 sm:text-base">
          {t('payments.firstDiscountDialog.eyebrow')}
        </div>
        <div className="text-center font-semibold text-[28px] text-system-white80 leading-[1.4] sm:text-lg">
          {t('payments.firstDiscountDialog.highlight')}
        </div>
      </div>
    );
  }

  // 剩余 7 天：两行文案居中，天数用琥珀色胶囊强调
  if (is7DaysLeft) {
    return (
      <div className={cn(bannerClass, 'gap-2.5 px-6 sm:gap-1.5 sm:px-4', className)} style={bgStyle(LAST_7D_BG)}>
        <div className="text-center font-semibold text-2xl text-white sm:text-base">
          {t('payments.mySubscription.retentionBannerTitle')}
        </div>
        <div className="text-center font-semibold text-[28px] text-white leading-[1.4] sm:text-lg">
          {t.rich('payments.mySubscription.retentionBannerExpireIn', {
            daysToExpiry,
            em: (chunks) => highlight(chunks, 'amber'),
          })}
        </div>
      </div>
    );
  }

  // 最后一天：左侧文案 + 右侧倒计时，窄屏回落为纵向
  return (
    <div
      className={cn(bannerClass, 'flex-row justify-between px-10 py-6 sm:flex-col sm:gap-4 sm:px-4', className)}
      style={bgStyle(LAST_1D_BG)}
    >
      <div className="flex flex-col items-start gap-1.5 sm:items-center sm:gap-2">
        <div className="font-semibold text-2xl text-white sm:text-base">
          {t('payments.mySubscription.retentionBannerTitle')}
        </div>
        <div className="font-semibold text-[28px] text-white leading-[1.4] sm:text-center sm:text-lg">
          {t.rich('payments.mySubscription.retentionBannerRenewWithin', {
            em: (chunks) => highlight(chunks, 'rose'),
          })}
        </div>
      </div>

      <div className="flex shrink-0 items-center gap-3 p-2 sm:gap-2 sm:p-0">
        <TimeBlock value={pad2(countdownHours)} label={t('payments.mySubscription.discountBannerCountdownHours')} />
        <div className="font-bold text-5xl text-system-white64 leading-none sm:text-2xl">:</div>
        <TimeBlock value={pad2(countdownMinutes)} label={t('payments.mySubscription.discountBannerCountdownMinutes')} />
        <div className="font-bold text-5xl text-system-white64 leading-none sm:text-2xl">:</div>
        <TimeBlock value={pad2(countdownSeconds)} label={t('payments.mySubscription.discountBannerCountdownSeconds')} />
      </div>
    </div>
  );
};
